import { Action } from 'redux';
import * as _ from 'underscore';

import { SiteUrls } from '@tableau/shared-urls';
import { OrderedCreateContentActions } from '../../app/spaces/Explorer/CreateContent/CreateContentActionsList';
import { TopLevelProjectId } from '../../app/state/SpaceInfo';
import { Messages } from '../../codegen/Localize';
import { ExploreContentApi_ContentTypeValue } from '../../ts/ServerApi/types/IContent';
import { HelpService } from '../../ts/services/HelpService';

// Used so we can identify action types in telemetry
export enum WelcomeScreenActions {
  createWorkbook = 'createWorkbook',
  startExploring = 'startExploring',
  downloadDesktop = 'downloadDesktop',
  downloadPrep = 'downloadPrep',
  manageProjects = 'manageProjects',
  manageUsers = 'manageUsers',
  dismiss = 'dismiss',
}

export type WelcomeScreenLinkDetails = {
  actionName: WelcomeScreenActions;
  linkTitle: string;
  openNewTab: boolean;
  url?: string;
  getAction?: () => Action;
};

// This is a class and not a namespace because it depends on the Localize object to be loaded beforehand.
// Using a namespace would try to initialize the object and call Messages before it was ready.
export class WelcomeScreenLinks {
  private siteUrls: SiteUrls;
  private locale: string;
  private queryParams: string;

  constructor(siteUrls: SiteUrls, helpService: HelpService) {
    this.siteUrls = siteUrls;
    this.locale = helpService.helpUrlLanguage();
    this.queryParams = helpService.getQueryParams();
  }

  createWorkbook(): WelcomeScreenLinkDetails {
    return {
      actionName: WelcomeScreenActions.createWorkbook,
      linkTitle: Messages.home_action_createWorkbook(),
      openNewTab: false,
      getAction: () => {
        const workbookContentType = ExploreContentApi_ContentTypeValue.workbook;
        const createWorkbookAction = _.find(OrderedCreateContentActions, a => a.contentType === workbookContentType);

        return createWorkbookAction.createReduxAction(TopLevelProjectId);
      }
    };
  }

  startExploring(): WelcomeScreenLinkDetails {
    return {
      actionName: WelcomeScreenActions.startExploring,
      linkTitle: Messages.home_welcomeScreen_ActionStartExploring(),
      openNewTab: false,
      url: this.siteUrls.exploreUrl(),
    };
  }

  downloadDesktop(): WelcomeScreenLinkDetails {
    return {
      actionName: WelcomeScreenActions.downloadDesktop,
      linkTitle: Messages.home_welcomeScreen_ActionDownloadDesktop(),
      openNewTab: true,
      url: 'https://www.tableau.com/' + this.locale + '/app/server-home-desktop?' + this.queryParams,
    };
  }

  downloadPrep(): WelcomeScreenLinkDetails {
    return {
      actionName: WelcomeScreenActions.downloadPrep,
      linkTitle: Messages.home_welcomeScreen_ActionDownloadPrep(),
      openNewTab: true,
      url: 'https://www.tableau.com/' + this.locale + '/app/server-home-prep?' + this.queryParams,
    };
  }

  manageProjects(): WelcomeScreenLinkDetails{
    return {
      actionName: WelcomeScreenActions.manageProjects,
      linkTitle: Messages.home_welcomeScreen_ActionManageProjects(),
      openNewTab: false,
      url: this.siteUrls.exploreUrl(),  // We want to drop them to the top-level projects, which is /explore
    };
  }

  manageUsers(): WelcomeScreenLinkDetails {
    return {
      actionName: WelcomeScreenActions.manageUsers,
      linkTitle: Messages.home_welcomeScreen_ActionManageUsers(),
      openNewTab: false,
      url: this.siteUrls.usersUrl(),
    };
  }
}
